import {cn} from "@/lib/utils.ts";
import {Link, useLocation} from "@tanstack/react-router";
import {type ReactNode, useState} from "react";
import {scrollToTop} from "@/utilities/utilities.ts";
import {featurePages, primaryLinks, sportPages} from "./siteNav.ts";

type MobileMenuLinkProps = {
    to?: string;
    children: ReactNode;
    className?: string;
    onClick?: any;
};

function MobileMenuLink({to, children, className, onClick = null}: MobileMenuLinkProps) {
    const [, setIsOpen] = useState(false);
    const location = useLocation();

    const isActive = location.pathname === to;

    return (
        <Link
            to={onClick ? undefined : to}
            onClick={() => {
                if (onClick) {
                    onClick();
                } else {
                    scrollToTop();
                    setIsOpen(false);
                }
            }}
            className={cn(
                "text-[#1F2022] text-lg hover:text-[#082F8A] transition",
                isActive && "text-[#082F8A] text-lg font-semibold border-b-2 border-[#082F8A] pb-1",
                className
            )}
        >
            {children}
        </Link>
    );
}

/**
 * The hamburger menu. `items="site"` lists exactly what SiteHeader shows on desktop (siteNav.ts),
 * for the home page and the other light-shell pages; the default is LandingNav's own set.
 */
const MobileMenu = ({buttonClassName = "xl:hidden text-white", items = "landing"}: {buttonClassName?: string; items?: "landing" | "site"}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [showFeaturesMenu, setShowFeaturesMenu] = useState(false);
    const [showLeaderboardsMenu, setShowLeaderboardsMenu] = useState(false);

    const toggleMenu = () => setIsOpen(!isOpen);
    const closeMenu = () => setIsOpen(false);

    return (
        <>
            {/* Hamburger Button */}
            <button
                onClick={toggleMenu}
                className={cn("p-2", buttonClassName)}
                aria-label="Menu"
            >
                <svg
                    className="w-8 h-8"
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                >
                    <path d="M4 6h16M4 12h16M4 18h16" />
                </svg>
            </button>

            {/* Mobile Menu Modal */}
            {isOpen && (
                <div data-cy={"landing-nav-mobile"} className="fixed inset-0 z-50 xl:hidden">
                    {/* Backdrop */}
                    <div
                        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
                        onClick={closeMenu}
                    />

                    {/* Menu Content */}
                    <div className="absolute top-0 right-0 bottom-0 w-full max-w-sm bg-white shadow-2xl flex flex-col overflow-y-auto">
                        {/* Close Button */}
                        <div className="flex justify-end p-6 shrink-0">
                            <button
                                onClick={closeMenu}
                                className="text-[#1F2022] hover:text-[#082F8A] transition"
                                aria-label="Close menu"
                            >
                                <svg
                                    className="w-8 h-8"
                                    fill="none"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                >
                                    <path d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        {/* Navigation Links: SiteHeader's own set, from the same list. */}
                        {items === "site" && <nav className="flex flex-col items-center gap-5 px-6 pt-2">
                            <MobileMenuLink onClick={() => setShowFeaturesMenu(!showFeaturesMenu)}>Features</MobileMenuLink>
                            {showFeaturesMenu &&
                                <div className={"w-full flex flex-col gap-1 py-2 px-4 border shadow-lg rounded-xl bg-gray-50"}>
                                    {featurePages.map((page) => (
                                        <MobileMenuLink key={page.to} to={page.to} className="text-sm! py-1.5 px-3 rounded-lg hover:bg-gray-100">
                                            {page.label}
                                        </MobileMenuLink>
                                    ))}
                                    <div className="mt-1 px-3 pt-2 border-t border-gray-200 text-xs font-semibold tracking-wide text-gray-500">SPORTS</div>
                                    <div className="grid grid-cols-2 gap-1">
                                        {sportPages.map((page) => (
                                            <MobileMenuLink key={page.to} to={page.to} className="text-sm! py-1.5 px-3 rounded-lg hover:bg-gray-100">
                                                {page.label}
                                            </MobileMenuLink>
                                        ))}
                                    </div>
                                </div>
                            }
                            {primaryLinks.map((link) => (
                                <MobileMenuLink key={link.label} to={link.to}>{link.label}</MobileMenuLink>
                            ))}
                        </nav>}

                        {items === "landing" && <nav className="flex flex-col items-center gap-5 px-6 pt-2">
                            <MobileMenuLink to="/">Home</MobileMenuLink>
                            <MobileMenuLink to="/about/">About Us</MobileMenuLink>
                            <MobileMenuLink onClick={() => setShowFeaturesMenu(!showFeaturesMenu)}>Features</MobileMenuLink>

                            {showFeaturesMenu &&
                                <div className={"w-full flex flex-col gap-1 py-2 px-4 border shadow-lg rounded-xl bg-gray-50"}>
                                    <MobileMenuLink to="/create-brackets/" className="text-sm! py-1.5 px-3 rounded-lg hover:bg-gray-100">
                                        Create Tournament Brackets
                                    </MobileMenuLink>
                                    <MobileMenuLink to="/padel-tournament-software/" className="text-sm! py-1.5 px-3 rounded-lg hover:bg-gray-100">
                                        Padel Tournament Software
                                    </MobileMenuLink>
                                    <MobileMenuLink to="/pickleball-tournament-software/" className="text-sm! py-1.5 px-3 rounded-lg hover:bg-gray-100">
                                        Pickleball Tournament Software
                                    </MobileMenuLink>
                                    <MobileMenuLink to="/tennis-tournament-software/" className="text-sm! py-1.5 px-3 rounded-lg hover:bg-gray-100">
                                        Tennis Tournament Software
                                    </MobileMenuLink>
                                    <MobileMenuLink to="/badminton-tournament-software/" className="text-sm! py-1.5 px-3 rounded-lg hover:bg-gray-100">
                                        Badminton Tournament Software
                                    </MobileMenuLink>
                                    <MobileMenuLink to="/squash-tournament-software/" className="text-sm! py-1.5 px-3 rounded-lg hover:bg-gray-100">
                                        Squash Tournament Software
                                    </MobileMenuLink>
                                    <MobileMenuLink to="/basketball-tournament-software/" className="text-sm! py-1.5 px-3 rounded-lg hover:bg-gray-100">
                                        Basketball Tournament Software
                                    </MobileMenuLink>
                                    <MobileMenuLink to="/football-tournament-software/" className="text-sm! py-1.5 px-3 rounded-lg hover:bg-gray-100">
                                        Football Tournament Software
                                    </MobileMenuLink>
                                    <MobileMenuLink to="/esports-tournament-software/" className="text-sm! py-1.5 px-3 rounded-lg hover:bg-gray-100">
                                        Esports Tournament Software
                                    </MobileMenuLink>
                                    <MobileMenuLink to="/volleyball-tournament-software/" className="text-sm! py-1.5 px-3 rounded-lg hover:bg-gray-100">
                                        Volleyball Tournament Software
                                    </MobileMenuLink>
                                    <MobileMenuLink to="/roundnet-tournament-software/" className="text-sm! py-1.5 px-3 rounded-lg hover:bg-gray-100">
                                        Roundnet Tournament Software
                                    </MobileMenuLink>
                                    <hr className="my-1 border-gray-200" />
                                    <MobileMenuLink to="/sports-analytics-software-for-clubs/" className="text-sm! py-1.5 px-3 rounded-lg hover:bg-gray-100">
                                        Sports Analytics For Clubs
                                    </MobileMenuLink>
                                    <MobileMenuLink to="/sports-tournament-management-software/" className="text-sm! py-1.5 px-3 rounded-lg hover:bg-gray-100">
                                        Tournament Management Software
                                    </MobileMenuLink>
                                    <MobileMenuLink to="/sports-league-management-software/" className="text-sm! py-1.5 px-3 rounded-lg hover:bg-gray-100">
                                        League Management Software
                                    </MobileMenuLink>
                                    <MobileMenuLink to="/live-scoring/" className="text-sm! py-1.5 px-3 rounded-lg hover:bg-gray-100">
                                        Live Scoring App
                                    </MobileMenuLink>
                                    <MobileMenuLink to="/for-clubs/" className="text-sm! py-1.5 px-3 rounded-lg hover:bg-gray-100">
                                        For Clubs
                                    </MobileMenuLink>
                                    <MobileMenuLink to="/for-communities/" className="text-sm! py-1.5 px-3 rounded-lg hover:bg-gray-100">
                                        For Communities
                                    </MobileMenuLink>
                                </div>
                            }

                            <Link
                                to="/"
                                hash="how-it-works"
                                onClick={() => {
                                    closeMenu();
                                    setTimeout(() => document.getElementById('how-it-works')?.scrollIntoView({behavior: 'smooth'}), 60);
                                }}
                                className="text-[#1F2022] text-lg hover:text-[#082F8A] transition"
                            >
                                How it Works
                            </Link>
                            <MobileMenuLink onClick={() => setShowLeaderboardsMenu(!showLeaderboardsMenu)}>Leaderboards</MobileMenuLink>

                            {showLeaderboardsMenu &&
                                <div className={"w-full flex flex-col gap-1 py-2 px-4 border shadow-lg rounded-xl bg-gray-50"}>
                                    <MobileMenuLink to="/leaderboards/leagues/" className="text-sm! py-1.5 px-3 rounded-lg hover:bg-gray-100">
                                        League of Leagues
                                    </MobileMenuLink>
                                    <MobileMenuLink to="/leaderboards/players/" className="text-sm! py-1.5 px-3 rounded-lg hover:bg-gray-100">
                                        League of Players
                                    </MobileMenuLink>
                                </div>
                            }

                            <MobileMenuLink to="/pricing/">Pricing</MobileMenuLink>
                            <MobileMenuLink to="/blog/">Blog</MobileMenuLink>
                            <MobileMenuLink to="/contact/">Contact</MobileMenuLink>
                        </nav>}

                        {/* Action Buttons */}
                        <div className="flex flex-col gap-4 px-6 mt-8 pb-10 shrink-0">
                            <MobileMenuLink to="/login/" className="w-full text-center bg-transparent border-2 border-[#082F8A] text-[#082F8A]! rounded-full px-8 py-3 text-base font-semibold hover:bg-[#082F8A]/10 transition"
                            >
                                Log In
                            </MobileMenuLink>

                            <MobileMenuLink to={"/signup/player/" as any} className="w-full text-center bg-[#082F8A] text-white! rounded-full px-8 py-3 text-base font-semibold hover:bg-[#082F8A]/90 transition"
                            >
                                Sign Up
                            </MobileMenuLink>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default MobileMenu;
